import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  LocationOn as LocationIcon,
  Timeline as TimelineIcon,
  PhotoCamera as PhotoIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { workOrdersApi } from '../../../services/api';
import WorkOrderTimeProgressBar from './WorkOrderTimeProgressBar';
import AuthenticatedImage from '../../common/AuthenticatedImage';
import AuthenticatedBackgroundImage from '../../common/AuthenticatedBackgroundImage'; // Import the new component
import DefectContextViewer from './DefectContextViewer';

const statusColors = {
  pending: 'default',
  assigned: 'info',
  in_progress: 'warning',
  overdue: 'error',
  completed: 'success',
  completed_temporary: 'warning',
  rejected: 'error',
  cancelled: 'default'
};

const priorityColors = {
  low: 'default',
  normal: 'info',
  high: 'warning',
  critical: 'error'
};

const WorkOrderDetailsModal = ({ open, onClose, workOrder }) => {
  const theme = useTheme();
  const [activities, setActivities] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailedWorkOrder, setDetailedWorkOrder] = useState(null);

  // State for image hover preview
  const [hoverPreview, setHoverPreview] = useState({ open: false, url: null, x: 0, y: 0 });

  // Constants for preview dimensions
  const PREVIEW_WIDTH = 380;
  const PREVIEW_HEIGHT = 280;

  // Function to compute preview position
  const computePreviewPos = useCallback((clientX, clientY) => {
    const x = Math.max(8, clientX - 16 - PREVIEW_WIDTH); // place to LEFT of cursor
    const maxY = (typeof window !== 'undefined' ? window.innerHeight : 0) - PREVIEW_HEIGHT - 8;
    const y = Math.min(Math.max(8, clientY + 16), Math.max(8, maxY));
    return { x, y };
  }, []);

  // Event handlers for image hover
  const handleImageMouseEnter = useCallback((url) => (e) => {
    console.log('Mouse Enter:', url);
    const { x, y } = computePreviewPos(e.clientX, e.clientY);
    setHoverPreview({ open: true, url, x, y });
  }, [computePreviewPos]);

  const handleImageMouseMove = useCallback((e) => {
    // console.log('Mouse Move'); // Too verbose, only log if needed
    const { x, y } = computePreviewPos(e.clientX, e.clientY);
    setHoverPreview((prev) => (prev.open ? { ...prev, x, y } : prev));
  }, [computePreviewPos]);

  const handleImageMouseLeave = useCallback(() => {
    console.log('Mouse Leave');
    setHoverPreview((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    const fetchDetails = async () => {
      if (open && workOrder) {
        try {
          setLoading(true);
          // Fetch the full work order details first
          const detailsResponse = await workOrdersApi.getWorkOrderById(workOrder.id);
          setDetailedWorkOrder(detailsResponse.data.data);

          // Then fetch activities and photos
          const [activitiesResponse, photosResponse] = await Promise.all([
            workOrdersApi.getActivities(workOrder.id),
            workOrdersApi.getPhotos(workOrder.id)
          ]);
          setActivities(activitiesResponse.data.data || []);
          setPhotos(photosResponse.data.data || []);
        } catch (error) {
          console.error('Failed to fetch work order details:', error);
          setDetailedWorkOrder(workOrder); // Fallback to initial data on error
          setActivities([]);
          setPhotos([]);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchDetails();
  }, [open, workOrder]);

  const displayWorkOrder = detailedWorkOrder || workOrder;

  if (!displayWorkOrder) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: theme.shadows[16],
          height: '90vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        py: 1.5,
        px: 2.5,
        flexShrink: 0,
        bgcolor: 'background.paper'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon color="primary" fontSize="small" />
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Action Log Progress Summary
          </Typography>
          <Chip
            label={displayWorkOrder.work_order_number}
            size="small"
            variant="outlined"
            sx={{ ml: 1, fontSize: '0.7rem' }}
          />
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{
        p: 0,
        display: 'flex',
        overflow: 'hidden',
        minHeight: '500px',
        maxHeight: 'calc(90vh - 120px)',
      }}>
        <Box sx={{ display: 'flex', width: '100%' }}>
          {/* Defect Context Viewer (Left Side) */}
          <Box sx={{
            width: '60%',
            display: 'flex',
            flexDirection: 'column',
            borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`
          }}>
            {/* Defect Context Header */}
            <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`, bgcolor: alpha(theme.palette.grey[50], 0.5) }}>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ fontSize: '0.875rem' }}>
                Defect Context & Visual Evidence
              </Typography>
              {displayWorkOrder && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {displayWorkOrder.defect_type_name} • {displayWorkOrder.location_description}
                </Typography>
              )}
            </Box>
            {/* Defect Context Content */}
            <Box sx={{ flex: 1, p: 1.5, overflow: 'auto' }}>
              <DefectContextViewer workOrder={displayWorkOrder} />
            </Box>
          </Box>

          {/* Work Order Details & Timeline (Right Side) */}
          <Box sx={{
            width: '40%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {loading ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
                py: 6
              }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading work order details...
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`, bgcolor: alpha(theme.palette.grey[50], 0.5) }}>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ fontSize: '0.875rem' }}>
                    Activity Timeline & Progress
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Status changes, notes, and photos
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                  {activities.length > 0 ? (
                    <Box sx={{ position: 'relative' }}>
                      <Box sx={{
                        position: 'absolute',
                        left: 15,
                        top: 0,
                        bottom: 0,
                        width: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                        borderRadius: 1
                      }} />
                      {activities
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .map((activity) => (
                          <Box key={activity.id} sx={{ position: 'relative', mb: 3, ml: 4 }}>
                            <Box sx={{
                              position: 'absolute',
                              left: -19,
                              top: 4,
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: activity.activity_type === 'status_update' ? 'primary.main' : 'info.main',
                              border: `2px solid white`,
                              boxShadow: 1,
                              zIndex: 1
                            }} />
                            <Box sx={{
                              bgcolor: alpha(theme.palette.background.paper, 0.8),
                              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              borderRadius: 2,
                              p: 2,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {activity.activity_type === 'status_update' ?
                                    `Status changed to: ${activity.new_status}` :
                                    activity.activity_type.replace('_', ' ')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(activity.created_at).toLocaleString()}
                                </Typography>
                              </Box>
                              {activity.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {activity.description}
                                </Typography>
                              )}
                              {activity.user_name && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PersonIcon fontSize="small" color="action" />
                                  <Typography variant="caption" color="text.secondary">
                                    by {activity.user_name}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  ) : (
                    <Box sx={{
                      textAlign: 'center',
                      py: 6,
                      color: 'text.secondary'
                    }}>
                      <TimelineIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                      <Typography variant="body1">
                        No activity recorded yet.
                      </Typography>
                    </Box>
                  )}
                  {photos.length > 0 && (
                    <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhotoIcon fontSize="small" />
                        Progress Photos ({photos.length})
                      </Typography>
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: 2
                      }}>
                        {photos.map((photo, index) => (
                          <Box
                            key={index}
                            sx={{
                              position: 'relative',
                              borderRadius: 2,
                              overflow: 'hidden',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                              }
                            }}
                          >
                            <AuthenticatedBackgroundImage
                              imageUrl={photo.file_path}
                              alt={photo.caption || `Photo ${index + 1}`}
                              sx={{
                                width: '100%',
                                height: '150px',
                                display: 'block',
                                cursor: 'pointer', // Indicate interactivity
                              }}
                              onMouseEnter={handleImageMouseEnter(photo.file_path)}
                              onMouseMove={handleImageMouseMove}
                              onMouseLeave={handleImageMouseLeave}
                            />
                            {photo.caption && (
                              <Box sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                bgcolor: 'rgba(0,0,0,0.8)',
                                color: 'white',
                                p: 1,
                                backdropFilter: 'blur(4px)'
                              }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.75rem',
                                    lineHeight: 1.2,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                  }}
                                >
                                  {photo.caption}
                                </Typography>
                              </Box>
                            )}
                            <Box sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.7rem',
                              fontWeight: 500
                            }}>
                              {new Date(photo.created_at).toLocaleDateString()}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{
        px: 2.5,
        py: 1.5,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        justifyContent: 'flex-end',
        flexShrink: 0,
        bgcolor: 'background.paper'
      }}>
        <Button
            onClick={onClose}
            color="inherit"
            size="medium"
          >
            Close
          </Button>
      </DialogActions>
      {hoverPreview.open && hoverPreview.url && (
        <Box
          sx={{
            position: 'fixed',
            top: hoverPreview.y,
            left: hoverPreview.x,
            width: PREVIEW_WIDTH,
            height: PREVIEW_HEIGHT,
            borderRadius: 1,
            boxShadow: '0 12px 28px rgba(0,0,0,0.3)',
            zIndex: theme.zIndex.modal + 1,
            pointerEvents: 'none', // Allows mouse events to pass through
            border: '1px solid',
            borderColor: alpha(theme.palette.grey[700], 0.4),
            backgroundColor: 'rgba(0,0,0,0.08)'
          }}
        >
          <AuthenticatedBackgroundImage
            imageUrl={hoverPreview.url}
            fit="contain"
            sx={{ width: '100%', height: '100%', borderRadius: 1, backgroundColor: 'black' }}
          />
        </Box>
      )}
    </Dialog>
  );
};

export default WorkOrderDetailsModal;